const API_BASE = import.meta.env.VITE_API_BASE;

interface User {
  id: string;
  name: string;
  firstName?: string;
  lastName?: string;
  email: string;
  role: 'admin' | 'staff' | 'volunteer' | 'member';
  permissions: string[];
  profilePhoto?: string;
  lastLogin?: string;
  isAdmin?: boolean;
  isInitialAdmin?: boolean;
  canInviteUsers?: boolean;
}

interface TeamMember {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  is_active: boolean;
  is_initial_admin: boolean;
  is_admin: boolean;
  can_invite_users: boolean;
  last_login_at?: string;
  created_at: string;
}

interface Church {
  id: string;
  name: string;
  subdomain: string;
  timezone: string;
  settings: any;
}

interface InviteLink {
  id: string;
  token: string;
  label?: string;
  expires_at?: string;
  max_uses?: number;
  use_count: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface TeamInviteLink {
  id: string;
  token: string;
  label?: string;
  role: string;
  expires_at?: string;
  max_uses?: number;
  usage_count: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface LoginResponse {
  success: boolean;
  accessToken: string;
  refreshToken: string;
  user: User;
  church: Church;
  requiresChurchSelection?: boolean;
  availableChurches?: Array<{ name: string; subdomain: string }>;
}

interface DashboardStats {
  date: string;
  serviceTime?: string;
  stats: {
    totalCheckins: number;
    currentlyCheckedIn: number;
    checkedOut: number;
    uniqueChildren: number;
    uniqueFamilies: number;
    avgDurationMinutes?: number;
  };
  breakdown: {
    byAgeGroup: Array<{
      ageGroup: string;
      checkinCount: number;
      currentlyCheckedIn: number;
    }>;
    byServiceTime: Array<{
      serviceTime: string;
      checkinCount: number;
      currentlyCheckedIn: number;
    }>;
  };
  recentActivity: Array<{
    id: string;
    status: string;
    childName: string;
    ageGroup: string;
    familyName: string;
    serviceTime: string;
    checkedInAt: string;
    checkedOutAt?: string;
    checkedInBy: string;
    checkedOutBy?: string;
  }>;
}

interface CheckIn {
  id: string;
  code: string;
  status: 'checked_in' | 'checked_out';
  serviceTime: string;
  checkedInAt: string;
  checkedOutAt?: string;
  location?: string;
  child: {
    id: string;
    firstName: string;
    lastName: string;
    birthdate: string;
    ageGroup: string;
    allergies?: string;
    medicalNotes?: string;
    specialInstructions?: string;
    photoUrl?: string;
  };
  family: {
    id: string;
    name: string;
    phone: string;
    emergencyContactName: string;
    emergencyContactPhone: string;
  };
  checkedInBy: string;
  checkedOutBy?: string;
}

// Families API types
interface Family {
  id: string;
  parentName: string;
  email: string;
  phone: string;
  emergencyContactName?: string;
  emergencyContactPhone?: string;
  notes?: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  children: Child[];
}

interface Child {
  id: string;
  firstName: string;
  lastName: string;
  birthdate: string;
  residence?: string;
  allergies?: string;
  medicalNotes?: string;
  specialInstructions?: string;
  status: string;
  createdAt: string;
  updatedAt: string;
}

interface ChurchOverview {
  totalFamilies: number;
  totalChildren: number;
  attendanceHistory: Array<{
    date: string;
    uniqueChildren: number;
    uniqueFamilies: number;
    totalCheckins: number;
  }>;
  services: Array<{
    id: string;
    name: string;
    description: string;
    weekday: number;
    start_time: string;
    duration_minutes: number;
    is_active: boolean;
  }>;
  recentRegistrations: Array<{
    id: string;
    parentName: string;
    email: string;
    childrenCount: number;
    registeredAt: string;
  }>;
}

class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public code?: string,
    public details?: any
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

class ChurchAdminApi {
  private token: string | null = null;
  
  constructor() {
    // Try to get token from localStorage on initialization
    this.token = localStorage.getItem('church_admin_token');
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${API_BASE}${endpoint}`;
    
    const headers: HeadersInit = {
      ...options.headers,
    };

    // Only set Content-Type to application/json if we have a body
    if (options.body) {
      (headers as Record<string, string>)['Content-Type'] = 'application/json';
    }

    // Add auth header if we have a token
    if (this.token) {
      (headers as Record<string, string>)['Authorization'] = `Bearer ${this.token}`;
    }

    try {
      const response = await fetch(url, {
        ...options,
        headers,
        credentials: 'include', // Include cookies for auth
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        
        // Handle auth errors (but not for login/register endpoints)
        if (response.status === 401 && !url.includes('/auth/login') && !url.includes('/register')) {
          this.clearAuth();
          window.location.href = '/';
          throw new ApiError('Authentication required', 401, 'UNAUTHORIZED');
        }
        
        throw new ApiError(
          errorData.message || `Request failed with status ${response.status}`,
          response.status,
          errorData.code,
          errorData
        );
      }

      return await response.json();
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      
      // Network errors
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new ApiError('Network error - please check your connection', 0, 'NETWORK_ERROR');
      }
      
      throw new ApiError('Unexpected error occurred', 500, 'UNKNOWN_ERROR');
    }
  }

  // Authentication
  async login(email: string, password: string, churchSubdomain?: string): Promise<LoginResponse> {
    const response = await this.request<LoginResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password, churchSubdomain }),
    });
    
    if (response.success && response.accessToken) {
      this.setAuth(response.accessToken);
    }
    
    return response;
  }

  async refreshToken(refreshToken: string): Promise<{ success: boolean; accessToken: string }> {
    const response = await this.request<{ success: boolean; accessToken: string }>('/auth/refresh', {
      method: 'POST',
      body: JSON.stringify({ refreshToken }),
    });
    
    if (response.success) {
      this.setAuth(response.accessToken);
    }
    
    return response;
  }

  async logout(): Promise<void> {
    try {
      await this.request('/auth/logout', { method: 'POST' });
    } finally {
      this.clearAuth();
    }
  }

  async getCurrentUser(): Promise<{ user: User; church: Church }> {
    return await this.request('/auth/me');
  }

  // Dashboard
  async getDashboardStats(date?: string, serviceTime?: string): Promise<DashboardStats> {
    const params = new URLSearchParams();
    if (date) params.set('date', date);
    if (serviceTime) params.set('service_time', serviceTime);
    
    const query = params.toString();
    return await this.request(`/dashboard/stats${query ? `?${query}` : ''}`);
  }

  async getCheckins(filters: {
    date?: string;
    serviceTime?: string;
    status?: 'checked_in' | 'checked_out' | 'all';
    ageGroup?: string;
    search?: string;
    page?: number;
    limit?: number;
  } = {}): Promise<{
    checkins: CheckIn[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
      hasNext: boolean;
      hasPrev: boolean;
    };
  }> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined) {
        params.set(key, value.toString());
      }
    });
    
    const query = params.toString();
    return await this.request(`/dashboard/checkins${query ? `?${query}` : ''}`);
  }

  async checkoutChild(checkinId: string): Promise<{
    success: boolean;
    message: string;
    checkin: { id: string; childName: string; familyName: string; status: string };
  }> {
    return await this.request(`/dashboard/checkins/${checkinId}/checkout`, {
      method: 'POST',
    });
  }

  async manualCheckin(childId: string, phoneNumber?: string): Promise<{
    success: boolean;
    alreadyCheckedIn: boolean;
    message: string;
    checkin: {
      id: string;
      code: string;
      childId: string;
      childName: string;
      familyName: string;
      status: string;
      allergies?: string;
      medicalNotes?: string;
      smsSent?: boolean;
      smsError?: string | null;
    };
  }> {
    return await this.request(`/dashboard/checkins/manual`, {
      method: 'POST',
      body: JSON.stringify({ childId, ...(phoneNumber ? { phoneNumber } : {}) }),
    });
  }

  async getEmergencyInfo(checkinId: string): Promise<{
    child: {
      name: string;
      birthdate: string;
      allergies?: string;
      medicalNotes?: string;
      specialInstructions?: string;
    };
    family: {
      name: string;
      email: string;
      phone: string;
      notes?: string;
    };
    emergencyContact: {
      name: string;
      phone: string;
    };
  }> {
    return await this.request(`/dashboard/checkins/${checkinId}/emergency`);
  }

  // Families
  async getFamilies(): Promise<{ success: boolean; families: Family[] }> {
    return await this.request('/families/families');
  }

  async addFamily(familyData: {
    parent_name: string;
    email: string;
    phone?: string;
    emergency_contact_name?: string;
    emergency_contact_phone?: string;
    notes?: string;
    children: Array<{
      first_name: string;
      last_name: string;
      birthdate: string;
      residence?: string;
      allergies?: string;
      medical_notes?: string;
      special_instructions?: string;
    }>;
  }): Promise<{ success: boolean; family?: Family; message?: string }> {
    return await this.request('/families/families', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(familyData),
    });
  }

  async deleteFamily(familyId: string): Promise<{ success: boolean; message: string }> {
    return await this.request(`/families/families/${familyId}`, {
      method: 'DELETE',
    });
  }

  async getChurchOverview(): Promise<{ success: boolean; overview: ChurchOverview }> {
    return await this.request('/families/overview');
  }

  // Admin Team management
  async getTeamMembers(): Promise<TeamMember[]> {
    const res = await this.request<{ success: boolean; users: TeamMember[] }>('/church/team');
    return res.users;
  }

  async inviteAdminUser(data: { firstName: string; lastName: string; email: string }): Promise<{ success: boolean; message: string; inviteUrl?: string }> {
    return await this.request('/church/team/invite', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async setCanInviteUser(userId: string, canInvite: boolean): Promise<{ success: boolean }> {
    return await this.request(`/church/team/${userId}/can-invite`, {
      method: 'PATCH',
      body: JSON.stringify({ canInvite }),
    });
  }

  async setAdminAccess(userId: string, isAdmin: boolean): Promise<{ success: boolean; user?: any }> {
    return await this.request(`/church/team/${userId}/admin-access`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ isAdmin }),
    });
  }

  async deactivateTeamMember(userId: string): Promise<{ success: boolean }> {
    return await this.request(`/church/team/${userId}/deactivate`, {
      method: 'PATCH',
    });
  }

  async deleteTeamMember(userId: string): Promise<{ success: boolean; deletedId: string }> {
    return await this.request(`/church/team/${userId}`, {
      method: 'DELETE',
    });
  }

  // Admin invite validation & acceptance (public – no auth token needed)
  async validateAdminInvite(token: string): Promise<{ email: string; firstName: string; lastName: string; churchName: string }> {
    return await this.request(`/auth/invite?token=${encodeURIComponent(token)}`);
  }

  async acceptAdminInvite(token: string, newPassword: string): Promise<{ success: boolean; message: string }> {
    return await this.request('/auth/invite/accept', {
      method: 'POST',
      body: JSON.stringify({ token, newPassword }),
    });
  }

  // Batch checkout
  async batchCheckoutChildren(ids: string[]): Promise<{ success: boolean; checkedOut: number; message: string }> {
    return await this.request('/dashboard/checkins/batch-checkout', {
      method: 'POST',
      body: JSON.stringify({ ids }),
    });
  }

  // Church invite links
  async getInviteLinks(): Promise<{ success: boolean; invites: InviteLink[] }> {
    return await this.request('/church/invite-links');
  }

  async createInviteLink(data: {
    label?: string;
    expires_at?: string;
    max_uses?: number;
  }): Promise<{ success: boolean; invite: InviteLink }> {
    return await this.request('/church/invite-links', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
  }

  // Team member invite links
  async getTeamInviteLinks(): Promise<{ success: boolean; invites: TeamInviteLink[] }> {
    return await this.request('/church/team/invite-links');
  }

  async createTeamInviteLink(data: {
    label?: string;
    role?: string;
    expires_at?: string;
    max_uses?: number;
  }): Promise<{ success: boolean; invite: TeamInviteLink }> {
    return await this.request('/church/team/invite-links', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
  }

  async deactivateTeamInviteLink(id: string): Promise<{ success: boolean }> {
    return await this.request(`/church/team/invite-links/${id}/deactivate`, {
      method: 'PATCH',
    });
  }

  async validateTeamInviteLink(token: string): Promise<{ success: boolean; churchName: string; label?: string; role?: string }> {
    return await this.request(`/auth/team-invite?token=${encodeURIComponent(token)}`);
  }

  async acceptTeamInviteLink(data: {
    token: string;
    firstName: string;
    lastName: string;
    email: string;
    password: string;
  }): Promise<{ success: boolean; message: string }> {
    return await this.request('/auth/team-invite/accept', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
  }

  // Settings / Location
  async updateChurchLocation(latitude: number, longitude: number): Promise<{ success: boolean; message: string }> {
    return await this.request('/church/location', {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ latitude, longitude }),
    });
  }

  // Auth management
  setAuth(token: string): void {
    this.token = token;
    localStorage.setItem('church_admin_token', token);
  }

  clearAuth(): void {
    this.token = null;
    localStorage.removeItem('church_admin_token');
  }

  isAuthenticated(): boolean {
    return !!this.token;
  }

  getToken(): string | null {
    return this.token;
  }
}

export const api = new ChurchAdminApi();
export type { User, Church, DashboardStats, CheckIn, LoginResponse, Family, Child, ChurchOverview, InviteLink, TeamInviteLink, TeamMember };
export { ApiError };